import { useState } from "react";
import { useNavigate, Link } from "react-router";
import { ArrowLeftCircleIcon } from "@heroicons/react/20/solid";
import { createProject } from "../api";
import { PACIFIC_COUNTRIES } from "../types";

const NewProjectPage = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const [formData, setFormData] = useState({
        name: "",
        country: "",
        locality: "",
        budget: ""
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            const projectData = {
                name: formData.name,
                country: formData.country,
                locality: formData.locality || undefined,
                budget: formData.budget ? parseFloat(formData.budget) : undefined
            };

            const newProject = await createProject(projectData);
            // Navigate to the newly created project page
            navigate(`/dashboard/project/${newProject.id}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create project');
            setIsSubmitting(false);
        }
    };

    return (
        <div className="h-full overflow-y-auto p-4">
            <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-600"
                    >
                        <ArrowLeftCircleIcon className="w-5 h-5 mr-2" />
                        Dashboard
                    </Link>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h1 className="text-2xl font-bold text-pacificblue-950 mb-6">
                        Create New Project
                    </h1>

                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Project Name *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pacificblue-500 focus:border-pacificblue-500"
                                placeholder="Enter project name"
                            />
                        </div>

                        <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                                Country *
                            </label>
                            <select
                                id="country"
                                name="country"
                                required
                                value={formData.country}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pacificblue-500 focus:border-pacificblue-500"
                            >
                                <option value="">Select a country</option>
                                {PACIFIC_COUNTRIES.map(country => (
                                    <option key={country.code} value={country.name}>
                                        {country.flag} {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="locality" className="block text-sm font-medium text-gray-700 mb-1">
                                Locality
                            </label>
                            <input
                                type="text"
                                id="locality"
                                name="locality"
                                value={formData.locality}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pacificblue-500 focus:border-pacificblue-500"
                                placeholder="Enter locality (optional)"
                            />
                        </div>

                        <div>
                            <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                                Budget (millions USD)
                            </label>
                            <input
                                type="number"
                                id="budget"
                                name="budget"
                                step="0.01"
                                value={formData.budget}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-pacificblue-500 focus:border-pacificblue-500"
                                placeholder="Enter budget in millions (optional)"
                            />
                        </div>

                        <div className="flex gap-4 pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-2 bg-pacificblue-600 text-white rounded-md hover:bg-pacificblue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isSubmitting ? 'Creating...' : 'Create Project'}
                            </button>
                            <Link
                                to="/dashboard"
                                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-center transition-colors"
                            >
                                Cancel
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NewProjectPage;
